// =======================
// src/paginas/modelos/componentes/modalNovoModelo.js
// =======================

import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const obterId = (item) => {
  if (typeof item === "object" && item !== null) {
    return item.id;
  }

  return item;
};

const obterIds = (itens) => {
  if (!Array.isArray(itens)) {
    return [];
  }

  return itens
    .map((item) => obterId(item))
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map(String);
};

const SelectMultiplo = ({
  label,
  itens,
  selecionados,
  onChange,
  disabled = false,
}) => {
  const nomesPorId = useMemo(
    () => new Map(itens.map((item) => [String(item.id), item.nome])),
    [itens],
  );

  const handleChange = (event) => {
    const valor = event.target.value;

    onChange(typeof valor === "string" ? valor.split(",") : valor);
  };

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>{label}</InputLabel>

      <Select
        multiple
        value={selecionados}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(valores) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {valores.map((id) => (
              <Chip
                key={id}
                size="small"
                label={nomesPorId.get(String(id)) || id}
                sx={{
                  height: 24,
                  backgroundColor: "#eaf4ff",
                  color: "#0767d8",
                }}
              />
            ))}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 320,
            },
          },
        }}
      >
        {itens.map((item) => {
          const id = String(item.id);

          return (
            <MenuItem key={item.id} value={id}>
              <Checkbox size="small" checked={selecionados.includes(id)} />
              <ListItemText primary={item.nome} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const ModalNovoModelo = ({
  open,
  onClose,
  categorias,
  assuntos,
  etapasModalidades,
  onSalvar,
  modelo = null,
}) => {
  const editando = Boolean(modelo?.id);

  const [titulo, setTitulo] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [assuntoId, setAssuntoId] = useState("");
  const [etapasModalidadesIds, setEtapasModalidadesIds] = useState([]);
  const [arquivo, setArquivo] = useState(null);
  const [arquivoInputKey, setArquivoInputKey] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const itensEtapasModalidades = useMemo(
    () =>
      [...etapasModalidades].sort((a, b) =>
        String(a.nome ?? "").localeCompare(String(b.nome ?? ""), "pt-BR"),
      ),
    [etapasModalidades],
  );

  const limparFormulario = () => {
    setTitulo("");
    setCategoriaId("");
    setAssuntoId("");
    setEtapasModalidadesIds([]);
    setArquivo(null);
    setErro("");

    setArquivoInputKey((valorAtual) => valorAtual + 1);
  };

  const carregarModelo = (modeloAtual) => {
    setTitulo(modeloAtual?.titulo ?? "");

    setCategoriaId(
      modeloAtual?.categoria_id ? String(modeloAtual.categoria_id) : "",
    );

    setAssuntoId(modeloAtual?.assunto_id ? String(modeloAtual.assunto_id) : "");

    setEtapasModalidadesIds([
      ...new Set([
        ...obterIds(modeloAtual?.etapas),
        ...obterIds(modeloAtual?.modalidades),
      ]),
    ]);

    setArquivo(null);
    setErro("");

    setArquivoInputKey((valorAtual) => valorAtual + 1);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    if (modelo?.id) {
      carregarModelo(modelo);
    } else {
      limparFormulario();
    }
  }, [open, modelo]);

  const handleFechar = () => {
    if (salvando) {
      return;
    }

    limparFormulario();
    onClose();
  };

  const handleSalvar = async () => {
    const tituloLimpo = titulo.trim();

    if (!tituloLimpo) {
      setErro("Informe o nome do modelo.");
      return;
    }

    if (!categoriaId) {
      setErro("Selecione a categoria.");
      return;
    }

    /*
     * No cadastro o arquivo é obrigatório.
     * Na edição ele só é enviado se o usuário
     * quiser substituir o arquivo atual.
     */
    if (!editando && !arquivo) {
      setErro("Selecione o arquivo do modelo.");
      return;
    }

    if (arquivo) {
      const extensaoPermitida = /\.(pdf|doc|docx|odt|xls|xlsx)$/i.test(
        arquivo.name,
      );

      if (!extensaoPermitida) {
        setErro(
          "Somente arquivos PDF, DOC, DOCX, ODT, XLS e XLSX são permitidos.",
        );
        return;
      }

      if (arquivo.size > 10 * 1024 * 1024) {
        setErro("O arquivo deve possuir no máximo 10 MB.");
        return;
      }
    }

    setSalvando(true);
    setErro("");

    try {
      await onSalvar({
        id: modelo?.id ?? null,
        titulo: tituloLimpo,
        categoriaId,
        assuntoId,

        // Mantém o contrato atual do envio durante a transição.
        // A seleção única vai em etapasIds; modalidadesIds vazio permite
        // remover também vínculos antigos desmarcados na edição.
        etapasIds: etapasModalidadesIds.map(Number),
        modalidadesIds: [],

        arquivo,
        editando,
      });

      limparFormulario();
      onClose();
    } catch (error) {
      console.error(
        editando ? "Erro ao editar modelo:" : "Erro ao cadastrar modelo:",
        error,
      );

      setErro(
        error.message ||
          (editando
            ? "Não foi possível atualizar o modelo."
            : "Não foi possível cadastrar o modelo."),
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleFechar}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      {/* ===============================================
          CABEÇALHO
      =============================================== */}

      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "#263238",
                fontWeight: 700,
              }}
            >
              {editando ? "Editar modelo" : "Novo modelo"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.25,
              }}
            >
              {editando
                ? "Atualize as informações e classificações do modelo."
                : "Informe a classificação e selecione o arquivo que será disponibilizado no catálogo."}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={handleFechar}
            disabled={salvando}
            aria-label="Fechar"
            sx={{
              color: "#607d8b",
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
        }}
      >
        {erro && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
            }}
          >
            {erro}
          </Alert>
        )}

        {/* ===============================================
            IDENTIFICAÇÃO
        =============================================== */}

        <Box
          sx={{
            mb: 2.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#0767d8",
              fontWeight: 700,
              mb: 1.5,
            }}
          >
            Identificação do modelo
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 1.5,
            }}
          >
            <TextField
              fullWidth
              size="small"
              required
              label="Nome do modelo"
              placeholder="Ex.: Requerimento de autorização"
              value={titulo}
              disabled={salvando}
              onChange={(event) => setTitulo(event.target.value)}
              inputProps={{
                maxLength: 255,
              }}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 1.5,
              }}
            >
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Categoria"
                value={categoriaId}
                disabled={salvando}
                onChange={(event) => setCategoriaId(event.target.value)}
              >
                <MenuItem value="">Selecione</MenuItem>

                {categorias.map((categoria) => (
                  <MenuItem key={categoria.id} value={String(categoria.id)}>
                    {categoria.nome}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                size="small"
                label="Assunto"
                value={assuntoId}
                disabled={salvando}
                onChange={(event) => setAssuntoId(event.target.value)}
              >
                <MenuItem value="">Sem assunto</MenuItem>

                {assuntos.map((assunto) => (
                  <MenuItem key={assunto.id} value={String(assunto.id)}>
                    {assunto.nome}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: 2.5,
          }}
        />

        {/* ===============================================
            ETAPAS E MODALIDADES
        =============================================== */}

        <Box
          sx={{
            mb: 2.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#0767d8",
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            Etapas e modalidades
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1.5,
            }}
          >
            Selecione uma ou mais opções quando forem aplicáveis ao modelo.
          </Typography>

          <SelectMultiplo
            label="Etapas e modalidades"
            itens={itensEtapasModalidades}
            selecionados={etapasModalidadesIds}
            onChange={setEtapasModalidadesIds}
            disabled={salvando || itensEtapasModalidades.length === 0}
          />

          {itensEtapasModalidades.length === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.5,
              }}
            >
              Nenhuma etapa/modalidade cadastrada.
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              backgroundColor: "#eaf4ff",
              border: "1px solid #c9def4",
            }}
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{
                color: "#087cf0",
                flexShrink: 0,
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: "#455a64",
              }}
            >
              As opções selecionadas serão vinculadas ao modelo.
            </Typography>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: 2.5,
          }}
        />

        {/* ===============================================
            ARQUIVO
        =============================================== */}

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#0767d8",
              fontWeight: 700,
              mb: 0.5,
            }}
          >
            {editando ? "Arquivo do modelo" : "Arquivo do modelo *"}
          </Typography>

          {editando && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1.5,
              }}
            >
              O envio de um novo arquivo é opcional. Se nenhum arquivo for
              selecionado, o arquivo atual será mantido.
            </Typography>
          )}

          <Box
            sx={{
              border: "1px dashed #9fcdff",
              borderRadius: 1.5,
              backgroundColor: "#fbfdff",
              px: 2,
              py: 2.5,
              textAlign: "center",
            }}
          >
            <CloudUploadRoundedIcon
              sx={{
                color: "#087cf0",
                fontSize: 34,
                mb: 0.75,
              }}
            />

            {editando && modelo?.nome_original && !arquivo && (
              <Box
                sx={{
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Arquivo atual
                </Typography>

                <Chip
                  size="small"
                  label={modelo.nome_original}
                  sx={{
                    maxWidth: "100%",
                    backgroundColor: "#eaf4ff",
                    color: "#0767d8",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                color: "#37474f",
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {editando
                ? "Substituir arquivo"
                : "Selecione o arquivo do modelo"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mb: 1.5,
              }}
            >
              PDF, DOC, DOCX, ODT, XLS ou XLSX — máximo de 10 MB
            </Typography>

            <Button
              component="label"
              variant="outlined"
              disabled={salvando}
              startIcon={<CloudUploadRoundedIcon />}
              sx={{
                textTransform: "none",
              }}
            >
              {editando ? "Selecionar novo arquivo" : "Selecionar arquivo"}

              <input
                key={arquivoInputKey}
                hidden
                type="file"
                accept=".pdf,.doc,.docx,.odt,.xls,.xlsx"
                onChange={(event) =>
                  setArquivo(event.target.files?.[0] || null)
                }
              />
            </Button>

            {arquivo && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 1.5,
                }}
              >
                <Chip
                  size="small"
                  label={arquivo.name}
                  onDelete={
                    salvando
                      ? undefined
                      : () => {
                          setArquivo(null);
                          setArquivoInputKey((valorAtual) => valorAtual + 1);
                        }
                  }
                  sx={{
                    maxWidth: "100%",
                    backgroundColor: "#eaf4ff",
                    color: "#0767d8",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      {/* ===============================================
          AÇÕES
      =============================================== */}

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          gap: 0.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleFechar}
          disabled={salvando}
          sx={{
            textTransform: "none",
          }}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleSalvar}
          disabled={salvando}
          sx={{
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          {salvando ? (
            <CircularProgress size={20} color="inherit" />
          ) : editando ? (
            "Salvar alterações"
          ) : (
            "Salvar modelo"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalNovoModelo;
